import {
  FLAG_NAMES,
  VARIABLE_NAMES,
  flagIsEnabled,
  getEnvVariable,
  getEnvironmentName,
} from "lib/environment";
import { useContext } from "react";
import {
  NeonSkyContext,
  NeonSkyFeatureFlag,
} from "lib/neon-sky/neon-sky-provider";
import { useGetMemberPermissionsQuery } from "types/generated/operations";
import { PERMISSION } from "types/RequiredPermissions";
import { useParams } from "react-router-dom";
import { useIsDisable1jActive } from "app/preserve-disable-1j";

export const DISABLE_1J_PARAM = "disable-1j";

let graphqlHost = flagIsEnabled(FLAG_NAMES.ENABLE_MESH)
  ? getEnvVariable(VARIABLE_NAMES.MESH_HOST)
  : import.meta.env.VITE_APP_GRAPHQL_HOST;

let clockworkwebHost = import.meta.env.VITE_APP_CLOCKWORK_WEB_HOST;
let staticAssetHost = import.meta.env.VITE_APP_CLOCKWORK_STATIC_ASSET_HOST;
const isPureLocal = import.meta.env.VITE_APP_MSW_ENABLED === "true";
const isProduction = getEnvironmentName() === "production";
const isStagingReadOnly =
  window.location.hostname === "staging-read-only.justworks.com";
const isSandboxReadOnly =
  window.location.hostname === "sandbox-read-only.justworks.com";
const asoProductionHost = "https://payroll.justworks.com";
const asoDevHost = "https://neon.dev.justworks.com";

function getHostFromPageHost(): string {
  const { port, protocol, hostname } = window.location;
  const portToUse = port ? `:${port}` : "";
  return `${protocol}//${hostname}${portToUse}`;
}

function buildPath(base: string, path: string): string {
  const normalizeSlashPath = path.replace("//", "/");
  const cleanPath = normalizeSlashPath.startsWith("/")
    ? normalizeSlashPath
    : `/${normalizeSlashPath}`;
  return `${base}${cleanPath}`;
}

function buildGraphQLPath(path: string): string {
  if (!graphqlHost || isPureLocal) {
    graphqlHost = getHostFromPageHost();
  }
  return buildPath(graphqlHost, path);
}

function buildWebPath(path: string): string {
  return path.includes("/cf/") ? path : buildClockworkWebPath(path);
}

function buildClockworkWebPath(path: string): string {
  if (!clockworkwebHost || isPureLocal) {
    clockworkwebHost = getHostFromPageHost();
  }
  return buildPath(clockworkwebHost, path);
}

function buildStaticAssetPath(path: string): string {
  if (!staticAssetHost || isPureLocal) {
    staticAssetHost = getHostFromPageHost();
  }
  return buildPath(staticAssetHost, `/${path}`);
}

function buildCsToolsPath(): string {
  if (isProduction) {
    return "https://cstools.justworks.com/internal";
  } else if (isStagingReadOnly) {
    return "https://staging.justworks.com/internal";
  } else if (isSandboxReadOnly) {
    return "https://sandbox.justworks.com/internal";
  } else {
    return buildWebPath("/internal");
  }
}

function buildASOPath(path: string): string {
  return isProduction ? `${asoProductionHost}${path}` : `${asoDevHost}${path}`;
}

enum CLOCKFACE_PATHS {
  COMPANY = "/company",
  EMPLOYER_OF_RECORD_INVITE = "/employer-of-record/invite",
  FIRMS = "/firms",
  FIRMS_INVITATION_NEW = "/firms/invitation/new",
  INVOICES = "/invoices",
  MEMBERS_CONTRACTORS = "/members/contractors",
  MEMBERS_EMPLOYEES = "/members/employees",
  VOUCHERS = "/vouchers",
}

/**
 * This is a mapping of paths to Neon Sky feature flags.
 * If the path is in this mapping, we will check if the feature flag is enabled.
 * If it is, we will redirect to the ASO path.
 * If it is not, we will redirect to the clockwork path.
 */
const ASO_PATH_MAPPING: Record<CLOCKFACE_PATHS, NeonSkyFeatureFlag> = {
  [CLOCKFACE_PATHS.COMPANY]: NeonSkyFeatureFlag.COMPANY_SETTINGS,
  [CLOCKFACE_PATHS.EMPLOYER_OF_RECORD_INVITE]:
    NeonSkyFeatureFlag.EOR_SOW_INVITE,
  [CLOCKFACE_PATHS.FIRMS]: NeonSkyFeatureFlag.MANAGE_EMPLOYEES,
  [CLOCKFACE_PATHS.FIRMS_INVITATION_NEW]: NeonSkyFeatureFlag.MANAGE_EMPLOYEES,
  [CLOCKFACE_PATHS.INVOICES]: NeonSkyFeatureFlag.INVOICES,
  [CLOCKFACE_PATHS.MEMBERS_CONTRACTORS]: NeonSkyFeatureFlag.MANAGE_EMPLOYEES,
  [CLOCKFACE_PATHS.MEMBERS_EMPLOYEES]: NeonSkyFeatureFlag.MANAGE_EMPLOYEES,
  [CLOCKFACE_PATHS.VOUCHERS]: NeonSkyFeatureFlag.VOUCHERS,
};

function useFeatureFlag({
  path,
  flag,
}: {
  path?: CLOCKFACE_PATHS;
  flag?: NeonSkyFeatureFlag;
}): boolean {
  const { featureFlags } = useContext(NeonSkyContext);
  const isDisable1jActive = useIsDisable1jActive();

  if (isDisable1jActive) {
    return false;
  }

  let featureFlag: NeonSkyFeatureFlag | undefined = undefined;

  if (path) {
    featureFlag = ASO_PATH_MAPPING[path];
  } else if (flag) {
    featureFlag = flag;
  }

  return Boolean(featureFlag && featureFlags[featureFlag]);
}

/**
 * This function is used to redirect to the clockwork path if the user is missing the required permissions.
 * It returns the clockwork path if the user is missing the required permissions, otherwise it returns undefined.
 */
function redirectToClockwork(
  path: CLOCKFACE_PATHS,
  permissions: PERMISSION[],
  requiredPermissions: PERMISSION[]
): string | undefined {
  const isMissingPermissions = !permissions.some((permission) =>
    requiredPermissions.includes(permission)
  );

  return isMissingPermissions ? buildClockworkWebPath(path) : undefined;
}

/**
 * These are paths that are still using Clockface.
 * We will redirect to the clockwork path for these paths.
 */
const CLOCKFACE_ORIGINAL_PATHS = [
  "/firms",
  "/firms/invitation/new",
  "/employer-of-record/invite",
];

function useNeonPath(path: CLOCKFACE_PATHS): string {
  const isFlagEnabled = useFeatureFlag({ path });
  const { data, error } = useGetMemberPermissionsQuery();

  if (isFlagEnabled && !error) {
    const cdmsCanonicalCompanyId =
      data?.authenticatedMember?.company?.cdmsCanonicalCompanyId || "";
    const permissions = data?.authenticatedMember.permissions || [];

    switch (path) {
      case CLOCKFACE_PATHS.MEMBERS_EMPLOYEES:
        // If the user is a manager and do not have these permissions, redirect to clockwork (because neon-sky is not ready for this yet)
        const redirectPath = redirectToClockwork(path, permissions, [
          PERMISSION.CHANGE_EMPLOYEES,
          PERMISSION.MAKE_PAYMENTS,
          PERMISSION.EMPLOYEE_INFO_FULL,
          PERMISSION.EXPENSES,
        ]);
        if (redirectPath) return redirectPath;
        return buildASOPath(`/manage/${cdmsCanonicalCompanyId}/employees`);

      case CLOCKFACE_PATHS.MEMBERS_CONTRACTORS:
        const contractorRedirectPath = redirectToClockwork(path, permissions, [
          PERMISSION.CHANGE_EMPLOYEES,
          PERMISSION.MAKE_PAYMENTS,
          PERMISSION.EMPLOYEE_INFO_FULL,
          PERMISSION.EXPENSES,
          PERMISSION.PAY_CONTRACTORS_VENDORS,
        ]);
        if (contractorRedirectPath) return contractorRedirectPath;
        return buildASOPath(`/manage/${cdmsCanonicalCompanyId}/contractors`);

      case CLOCKFACE_PATHS.FIRMS:
        return buildASOPath(
          `/manage/${cdmsCanonicalCompanyId}/third-party-admins`
        );

      case CLOCKFACE_PATHS.FIRMS_INVITATION_NEW:
        return buildASOPath(`/manage/${cdmsCanonicalCompanyId}/invite-firm`);

      case CLOCKFACE_PATHS.COMPANY:
        return buildASOPath(`/company/${cdmsCanonicalCompanyId}/settings`);

      case CLOCKFACE_PATHS.EMPLOYER_OF_RECORD_INVITE:
        return buildASOPath(
          `/manage/${cdmsCanonicalCompanyId}/invite-eor-employee`
        );

      case CLOCKFACE_PATHS.VOUCHERS:
        return buildASOPath("/account/vouchers");

      case CLOCKFACE_PATHS.INVOICES:
        return buildASOPath(`/payments/${cdmsCanonicalCompanyId}/payrolls`);

      default:
        return buildClockworkWebPath(path);
    }
  }

  if (CLOCKFACE_ORIGINAL_PATHS.includes(path)) {
    return path;
  }

  return buildClockworkWebPath(path);
}

function useEorOffboardingPath(): string {
  const { id } = useParams();
  return buildASOPath(`/member/${id}/ieor-termination`);
}

export {
  buildASOPath,
  buildClockworkWebPath,
  buildCsToolsPath,
  buildGraphQLPath,
  buildStaticAssetPath,
  buildWebPath,
  CLOCKFACE_PATHS,
  useFeatureFlag,
  useNeonPath,
  useEorOffboardingPath,
};
